/**
 * The pre-gate terms disclosure acknowledgement — CU-86ak6ez40, Phase D.
 *
 * ⚠️ THIS IS A DISCLOSURE GATE, NOT AN ATTESTATION. It records that the patient
 * was SHOWN the terms before the MFA enrolment gate. It is not a signature, it
 * produces no artifact, and nothing outside this browser tab ever learns it
 * happened. The legally-operative signature is still taken post-AAL2 on
 * `info/consentForms`, unchanged.
 *
 * ── ⚠️ WHY THIS IS `sessionStorage` AND NOT A CHART WRITE ─────────────────
 * The consent flow confirms every write by RE-READING the chart. Post-cutover a
 * gated patient cannot read the chart, so that read-back never resolves — six
 * measured break points (`PENDING_EULA_PREGATE.md`, 2026-08-27), including a
 * form that re-presents as unsigned after a SUCCESSFUL write and can mint
 * duplicate `ConsentHistory` entries.
 *
 * ⚠️ Writing NOTHING sidesteps all six. Do not add a Chart write, a callable, or
 * a user-doc field "for completeness" — a write creates a read-back
 * expectation, and the read-back is the bug. (Shaun's ruling, 2026-08-31.)
 *
 * ── ⚠️ SESSION-SCOPED IS A REQUIREMENT, NOT AN IMPLEMENTATION DETAIL ──────
 * `sessionStorage` is per-tab and dies with it. It survives a page refresh
 * mid-enrolment (the point — a reload must not re-present the terms), but does
 * NOT persist across sessions and MUST NOT. A patient who abandons and returns
 * sees the disclosure again, which is correct: they still have not signed
 * anything, and this was never a record that they had.
 *
 * ⚠️ Do NOT "upgrade" this to `localStorage`. That would make an
 * acknowledgement that writes nothing server-side look durable, which is
 * precisely the claim this design refuses to make.
 */

/**
 * Keyed per uid. Two patients sharing a browser tab (a caregiver signing out
 * and back in) must not inherit each other's acknowledgement.
 */
const keyFor = (uid: string) => `mfaTermsDisclosureAck:${uid}`;

/**
 * Has this patient acknowledged the terms IN THIS SESSION?
 *
 * ⚠️ FAILS OPEN TO SHOWING THE DISCLOSURE. Private browsing and blocked
 * site-data both THROW on access — not return null. On any failure we return
 * false, which shows the terms again. Showing them twice is a papercut;
 * skipping them is the compliance failure this screen exists to prevent.
 */
export function hasAcknowledgedTerms(uid: string | null | undefined): boolean {
  if (!uid) return false;
  try {
    return window.sessionStorage.getItem(keyFor(uid)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Record the acknowledgement for the rest of this tab's session.
 *
 * ⚠️ A THROW HERE MUST NOT BLOCK ENROLMENT. If storage is unavailable the
 * patient still proceeds — the caller holds the acknowledgement in React state
 * for the life of the mount, so the only cost is re-presenting the terms after
 * a refresh. Refusing to advance would strand a private-browsing patient
 * outside their own PHI, which is a worse outcome than a repeated screen.
 */
export function recordTermsAcknowledgement(uid: string | null | undefined): void {
  if (!uid) return;
  try {
    window.sessionStorage.setItem(keyFor(uid), 'true');
  } catch {
    // Intentionally swallowed. See above — enrolment is never blocked by
    // storage. There is no Sentry breadcrumb here on purpose: this fires on
    // every private-browsing session and would be pure noise.
  }
}
